import React, { useEffect, useState } from 'react'
import { useAppDispatch } from '../../../redux/store'
import { addError, addWarning } from '../../../redux/alerts'
import PageHeader from '../../../components/PageHeader'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import TrainingComplianceReport from './TrainingComplianceReport'
import TrainingDue from './TrainingDue'
import { trackPromise } from 'react-promise-tracker'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getCorpChildrenTrainings } from './services'
import { exportAllTraining, exportTrainingByPractice, exportTrainingByYear } from './Reports'

import useCustomerNumber from '../../../hooks/useCustomerNumber'
import { ComplianceStatus } from '../../../types'

const initUserTrainingStatus = {
  pastDue: 0,
  dueSoon: 0,
  completed: 0
}

function TrainingDashboard() {
  const dispatch = useAppDispatch()
  const pageTabs = ['Compliance Report', 'Past Due', 'Training Due']
  const [courses, setCourses] = useState([])
  const [customers, setCustomers] = useState([])
  const [exportOpenEl, setExportOpenEl] = useState(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [userTrainingStatus, setUserTrainingStatus] = useState(initUserTrainingStatus)
  const exportOpen = Boolean(exportOpenEl)
  const customerNumber = useCustomerNumber()

  const getCustomers = async () => {
    try {
      const children = await getCorpChildrenTrainings(customerNumber)
      const trainingStatus = { ...initUserTrainingStatus }

      let courses = {}

      children.forEach((customer) => {
        customer.training_compliance_status = customer?.customer_training_metadata?.training_compliance_status || 4
        customer.training_completed_count = customer?.customer_training_metadata?.current_count ?? 0
        customer.training_due_soon_count = customer?.customer_training_metadata?.due_soon_count ?? 0
        customer.training_past_due_count = customer?.customer_training_metadata?.past_due_count ?? 0

        if (customer.users_training.length > 0) {
          customer.users_training.forEach((course) => {
            if (!Object.hasOwn(courses, course.course_id)) {
              courses[course.course_id] = course?.training?.name || 'Unknown'
            }
          })
        }
        trainingStatus.completed += customer.training_completed_count
        trainingStatus.dueSoon += customer.training_due_soon_count
        trainingStatus.pastDue += customer.training_past_due_count
      })

      courses = Object.keys(courses)
        .map((key) => {
          return {
            name: courses[key],
            value: key
          }
        })
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))

      setCourses([...courses])
      setUserTrainingStatus({ ...trainingStatus })
      setCustomers(children)
      return true
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load training data'
      dispatch(addError(message))
      return false
    }
  }

  const handleExport = async (exportFn, errorMessage) => {
    handleExportClose()

    try {
      const exported = await exportFn(customerNumber)

      if (!exported) {
        dispatch(addWarning('There are no trainings to download'))
      }
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || errorMessage
      dispatch(addError(message))
    }
  }

  const handleAllTraining = () => handleExport(exportAllTraining, 'Failed to load training data')
  const handleTrainingByPractice = () => handleExport(exportTrainingByPractice, 'Failed to load training data')
  const handleTrainingByYear = () => handleExport(exportTrainingByYear, 'Failed to load training by year report')

  const handleExportClick = (event) => {
    setExportOpenEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setExportOpenEl(null)
  }

  useEffect(() => {
    if (customerNumber) {
      trackPromise(getCustomers())
    }
  }, [customerNumber])

  const handleChange = (_event, newValue) => {
    setTabIndex(newValue)
  }

  const tabPages = [
    <TrainingComplianceReport key="training-compliance-report" customers={customers} userTrainingStatus={userTrainingStatus} courses={courses} />,
    <TrainingDue key="training-past-due" customers={customers} complianceStatus={ComplianceStatus.PastDue} message="There are no past due courses" />,
    <TrainingDue key="training-due" customers={customers} complianceStatus={ComplianceStatus.Due} message="There are no due courses" />
  ]

  return (
    <div>
      <PageHeader iconName={'graduation-cap'} headerName={'Training'} />
      <div>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-space-between flex-align-center">
            <Tabs value={tabIndex} onChange={handleChange} aria-label="basic tabs example">
              {pageTabs.length && pageTabs.map((tab, index) => <Tab key={index} label={tab} {...index} />)}
            </Tabs>
            <div className="dropdown">
              <Button className="m-r-12" aria-haspopup="true" variant="contained" name="export" onClick={handleExportClick}>
                Export
                <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" />
              </Button>
              <Menu
                id="account-menu"
                anchorEl={exportOpenEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                open={exportOpen}
                onClose={handleExportClose}
                MenuListProps={{
                  'aria-labelledby': 'export-button'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <MenuItem onClick={handleAllTraining}>All Training</MenuItem>
                <MenuItem onClick={handleTrainingByPractice}>Training by Course Type</MenuItem>
                <MenuItem onClick={handleTrainingByYear}>Training by Year</MenuItem>
              </Menu>
            </div>
          </Box>
          <Box className="m-12">{tabPages[tabIndex]}</Box>
        </Box>
      </div>
    </div>
  )
}

export default TrainingDashboard
